import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, ArrowUp, ArrowDown, Minus, Sparkles, Star } from 'lucide-react';
import SqueezeRadarPill from '@/components/SqueezeRadarPill';
import MinerviniBadge from '@/components/MinerviniBadge';
import AbbrTip from '@/components/AbbrTip';
import usePriceStream from '@/hooks/usePriceStream';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREFS_KEY = 'dividrip:capitalLeaderboard:prefs';
const ALLOWED_LIMITS = [3, 5, 10];

const loadLimitPref = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return 3;
    const obj = JSON.parse(raw) || {};
    return ALLOWED_LIMITS.includes(obj.limit) ? obj.limit : 3;
  } catch { return 3; }
};

/**
 * CapitalAnalyticsLeaderboard — Feb 2026.
 *
 * Dashboard widget showing the top-N non-dividend tickers by their
 * weekly Capital Quality Rank (1-100). Powered by
 * `/api/stocks/capital-quality-leaderboard` which is a pure Mongo
 * read against `stock_cache.equity_quality.*` — no upstream calls,
 * no LLM cost, no warmup. Refreshed weekly by the EQR cron.
 *
 * Each row shows ticker · sector · current rank · week-over-week
 * delta arrow. Click → opens the same StockModal pipeline as the
 * Dashboard's other widgets so users land directly on the Capital
 * Analytics tab.
 *
 * Size buttons (3 / 5 / 10) mirror the TopMoversWidget pattern so
 * users can expand the list on demand. We always fetch 10 from the
 * server (single round-trip) and slice client-side, so switching
 * sizes is instant. Choice persists via localStorage.
 *
 * `previous_rank` ships null on the first cron run that wrote it;
 * we render a neutral "new" pill in that case instead of an arrow.
 */
export default function CapitalAnalyticsLeaderboard({ onStockClick, watchlist = [], onToggleWatchlist }) {
  const [items, setItems] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(loadLimitPref);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/stocks/capital-quality-leaderboard`, { params: { limit: 10 } })
      .then((r) => {
        if (cancelled) return;
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setAsOf(r.data?.as_of || null);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ limit })); } catch { /* noop */ }
  }, [limit]);

  // Round P108ai (Feb 2026) — live-price subscription. Hooks MUST
  // come before any conditional early-return per React's rules of
  // hooks. Depends on `items` (raw) rather than `visibleItems`
  // (sliced) so the ticker list stays stable across size-toggle
  // changes.
  const visibleTickers = useMemo(
    () => items.slice(0, limit).map((r) => r.ticker),
    [items, limit],
  );
  const { prices: livePrices } = usePriceStream(visibleTickers);

  if (error) return null;  // silent fail — widget is supplementary

  const visibleItems = items.slice(0, limit);

  return (
    <div
      className="rounded-xl border border-purple-500/50 bg-card p-4 sm:p-5"
      data-testid="capital-analytics-leaderboard"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-purple-500 shrink-0" />
          <h3 className="text-[18px] font-semibold tracking-tight truncate">
            Capital Quality Top {limit}
          </h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div
            className="inline-flex rounded-md border border-border"
            data-testid="capital-analytics-leaderboard-size-toggle"
          >
            {ALLOWED_LIMITS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setLimit(n)}
                data-testid={`capital-leaderboard-limit-${n}`}
                aria-pressed={limit === n}
                className={`px-2 py-0.5 text-[11px] font-mono font-semibold transition-colors ${
                  limit === n
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          {asOf && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Week of {asOf}
            </span>
          )}
        </div>
      </div>
      <p className="text-[13px] text-muted-foreground mb-3">
        Highest-rated non-dividend stocks this week — by DiviDrip&apos;s{' '}
        {/* Round P108ai — AbbrTip with glossary link so users can
            learn what the 4-factor score actually measures and how the
            0–100 rank is computed. */}
        <AbbrTip term="CAPITAL_QUALITY_MODEL">4-factor Capital Quality model</AbbrTip>.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading leaderboard…
        </div>
      ) : visibleItems.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4">
          The weekly Capital Analytics model hasn&apos;t run yet. Check back after
          the next Sunday refresh.
        </p>
      ) : (
        // Round P108ai — horizontal scroll wrapper for mobile. Table
        // headers + per-factor columns don't have to fit in 375px;
        // users swipe left/right to view. Same pattern as our other
        // scrollable dashboard tables.
        <div className="-mx-4 sm:-mx-5 overflow-x-auto" data-testid="capital-analytics-leaderboard-scroll">
          <table className="w-full min-w-[860px] text-[12px] sm:text-[13px]" data-testid="capital-analytics-leaderboard-table">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
              <tr>
                <th className="w-8" aria-label="Watchlist"></th>
                <th className="w-8 text-right pr-2">
                  <AbbrTip term="RANK_POSITION">#</AbbrTip>
                </th>
                <th className="text-left px-2 py-2">Ticker · Sector</th>
                <th className="text-right px-2 py-2">
                  <AbbrTip term="CAPITAL_QUALITY_MODEL">Quality</AbbrTip>
                </th>
                <th className="text-right px-2 py-2">
                  <AbbrTip term="WOW_DELTA">Δ WoW</AbbrTip>
                </th>
                <th className="text-right px-2 py-2">Price</th>
                <th className="text-right px-2 py-2 whitespace-nowrap">
                  <AbbrTip term="SHAREHOLDER_YIELD">Buyback %</AbbrTip>
                </th>
                <th className="text-right px-2 py-2 whitespace-nowrap">
                  <AbbrTip term="CFROI">CFROI %</AbbrTip>
                </th>
                <th className="text-right px-2 py-2 whitespace-nowrap">
                  <AbbrTip term="OPERATING_MOMENTUM">Op. Mom.</AbbrTip>
                </th>
                <th className="text-right px-2 py-2 pr-4 whitespace-nowrap">
                  <AbbrTip term="ALTMAN_Z">Altman Z</AbbrTip>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60" data-testid="capital-analytics-leaderboard-list">
              {visibleItems.map((row, idx) => {
                const inWatchlist = watchlist.includes(row.ticker);
                const live = livePrices?.[row.ticker];
                const displayPrice = live?.price != null ? live.price : row.price;
                const isLive = live?.price != null;
                return (
                  <tr key={row.ticker} className="hover:bg-muted/40 transition-colors">
                    <td className="pl-4 sm:pl-5 py-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggleWatchlist?.(row.ticker); }}
                        className="p-1 rounded hover:bg-muted/60 transition-colors"
                        data-testid={`watchlist-star-${row.ticker}`}
                        title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                        aria-label={inWatchlist ? `Remove ${row.ticker} from watchlist` : `Add ${row.ticker} to watchlist`}
                        aria-pressed={inWatchlist}
                      >
                        <Star className={`w-4 h-4 transition-colors ${inWatchlist ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} />
                      </button>
                    </td>
                    <td className="text-right pr-2 font-mono tabular-nums text-muted-foreground text-[11px]">
                      {idx + 1}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => onStockClick?.(row.ticker)}
                          className="text-sm font-semibold hover:underline underline-offset-2"
                          data-testid={`capital-analytics-leaderboard-row-${row.ticker}`}
                        >
                          {row.ticker}
                        </button>
                        <SqueezeRadarPill ticker={row.ticker} />
                        {row.minervini?.sepa_pass && (
                          <>
                            <MinerviniBadge ticker={row.ticker} m={row.minervini} kind="rs" />
                            {row.minervini.tight_base && <MinerviniBadge ticker={row.ticker} m={row.minervini} kind="tight" />}
                          </>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-[220px]">
                        {row.sector || '—'}
                      </div>
                    </td>
                    <td className="text-right px-2 py-2 font-mono font-semibold tabular-nums" data-testid={`capital-leaderboard-quality-${row.ticker}`}>
                      {Math.round(row.rank)}
                    </td>
                    <td className="text-right px-2 py-2 whitespace-nowrap">
                      <DeltaPill delta={row.delta} previousRank={row.previous_rank} />
                    </td>
                    <td
                      className={`text-right px-2 py-2 font-mono tabular-nums ${isLive ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}
                      data-testid={`capital-leaderboard-price-${row.ticker}`}
                      title={isLive ? 'Live price via Massive WebSocket' : 'Weekly snapshot price'}
                    >
                      {displayPrice != null ? `$${Number(displayPrice).toFixed(2)}` : '—'}
                    </td>
                    <td className="text-right px-2 py-2 font-mono tabular-nums text-muted-foreground" data-testid={`capital-leaderboard-buyback-${row.ticker}`}>
                      {row.buyback_yield != null ? `${(Number(row.buyback_yield) * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="text-right px-2 py-2 font-mono tabular-nums text-muted-foreground" data-testid={`capital-leaderboard-cfroi-${row.ticker}`}>
                      {row.cfroi != null ? `${(Number(row.cfroi) * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="text-right px-2 py-2 font-mono tabular-nums text-muted-foreground" data-testid={`capital-leaderboard-momentum-${row.ticker}`}>
                      {row.operating_momentum != null ? Number(row.operating_momentum).toFixed(2) : '—'}
                    </td>
                    <td className="text-right px-2 py-2 pr-4 font-mono tabular-nums text-muted-foreground" data-testid={`capital-leaderboard-altman-${row.ticker}`}>
                      {row.altman_z != null ? Number(row.altman_z).toFixed(2) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeltaPill({ delta, previousRank }) {
  if (previousRank == null) {
    return (
      <span
        title="First week ranked — no delta yet"
        className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-1.5 py-0.5"
        data-testid="capital-analytics-delta-new"
      >
        new
      </span>
    );
  }
  if (delta == null || Math.abs(delta) < 0.05) {
    return (
      <span
        title="No change vs last week"
        className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"
        data-testid="capital-analytics-delta-flat"
      >
        <Minus className="w-3 h-3" />
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      title={`${up ? 'Up' : 'Down'} ${Math.abs(delta).toFixed(1)} pts vs last week`}
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums ${
        up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
      }`}
      data-testid={`capital-analytics-delta-${up ? 'up' : 'down'}`}
    >
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(delta).toFixed(1)}
    </span>
  );
}
